import { ExportV2 } from "@robin/common";
import { useMemo } from "react";
import { Datapoint, SeriesType } from "./useVisualisationData";
import { CurveType } from "@mui/x-charts/models";
import { useTheme } from "@mui/material";

export const useCalculatedChartData = (
  series: SeriesType[],
  legend: {
    focused?: null | string;
    hidden?: null | string[];
  },
  showNight: boolean,
  showMidnight: boolean,
  dataset?: Datapoint[]
) => {
  const theme = useTheme();

  const chartData: {
    timestamp: ExportV2.Timestamp;
    [key: string]: number | null;
  }[] = useMemo(() => {
    if (!dataset) return [];

    const DAY_START = 6;
    const NIGHT_START = 22;
    const NIGHT_VALUE = 10;
    const NIGHT_END_VALUE = null;

    // Find the first and last timestamp to ensure we have continuous data for every day
    const timestamps = dataset.map((item) => item.timestamp);
    const firstDate = new Date(Math.min(...timestamps));
    const lastDate = new Date(Math.max(...timestamps));

    const daysInRange = [];
    for (
      let date = new Date(firstDate);
      date <= lastDate;
      date.setDate(date.getDate() + 1)
    ) {
      daysInRange.push(new Date(date));
    }

    return daysInRange
      .reduce<
        { timestamp: ExportV2.Timestamp; [key: string]: number | null }[]
      >((acc, date) => {
        // For each day, calculate the necessary transition points
        const dayStartTimestamp = new Date(
          date.getFullYear(),
          date.getMonth(),
          date.getDate(),
          DAY_START, // 6 AM
          0,
          0,
          0
        ).getTime();

        const dayEndTimestamp = new Date(
          date.getFullYear(),
          date.getMonth(),
          date.getDate(),
          DAY_START, // 6:01 AM (null value, daytime starts)
          1,
          0,
          0
        ).getTime();

        const nightTransitionTimestamp = new Date(
          date.getFullYear(),
          date.getMonth(),
          date.getDate(),
          NIGHT_START - 1, // 9:59 PM (null value, daytime ends)
          59,
          0,
          0
        ).getTime();

        const nightStartTimestamp = new Date(
          date.getFullYear(),
          date.getMonth(),
          date.getDate(),
          NIGHT_START, // 10 PM
          0,
          0,
          0
        ).getTime();

        // Add necessary points for each day
        acc.push({ timestamp: dayStartTimestamp, night: NIGHT_VALUE });
        acc.push({ timestamp: dayEndTimestamp, night: NIGHT_END_VALUE });
        acc.push({
          timestamp: nightTransitionTimestamp,
          night: NIGHT_END_VALUE,
          midnight: NIGHT_VALUE,
        });
        acc.push({ timestamp: nightStartTimestamp, night: NIGHT_VALUE });

        // Midnight series points
        const midnightStartTimestamp = new Date(
          date.getFullYear(),
          date.getMonth(),
          date.getDate(),
          23, // 23:59
          55,
          0,
          0
        ).getTime();
        const midnightEndTimestamp = new Date(
          date.getFullYear(),
          date.getMonth(),
          date.getDate() + 1, // Move to next day
          0,
          1,
          0,
          0
        ).getTime();
        acc.push({
          timestamp: midnightStartTimestamp,
          night: NIGHT_VALUE,
          midnight: NIGHT_VALUE,
        });
        acc.push({
          timestamp: midnightEndTimestamp,
          night: NIGHT_VALUE,
          midnight: NIGHT_VALUE,
        });

        // Check and add missing "night" values between existing data points
        dataset.forEach((item) => {
          const itemDate = new Date(item.timestamp);

          // Check if this data point is within the same day as the current date
          if (
            itemDate.getFullYear() === date.getFullYear() &&
            itemDate.getMonth() === date.getMonth() &&
            itemDate.getDate() === date.getDate()
          ) {
            const hours = itemDate.getHours();

            // Determine if the timestamp falls into the night period
            const isNight = hours >= NIGHT_START || hours < DAY_START;
            const nightValue = isNight ? NIGHT_VALUE : NIGHT_END_VALUE;

            // Flatten the "values" field to extract only the numeric "value"
            const flattenedValues = Object.entries(item.values).reduce<
              Record<string, number>
            >((acc, [key, val]) => {
              acc[key] = val.value;
              return acc;
            }, {});

            // Add marks if activities or experiences are present
            const extrasMark = { extras: -0.4 };
            const hasExtras =
              item.extras.positiveActivities.length ||
              item.extras.experiences.length;

            acc.push({
              timestamp: item.timestamp,
              night: nightValue,
              midnight: nightValue,
              ...flattenedValues,
              ...(hasExtras && extrasMark),
            });
          }
        });

        return acc;
      }, [])
      .sort((a, b) => a.timestamp - b.timestamp);
  }, [dataset]);

  const chartSeries = [
    ...series
      .filter((serie) => !legend.hidden?.includes(serie.id))
      .map((serie) => ({
        color: serie.color,
        connectNulls: true,
        curve: "monotoneX" as CurveType,
        dataKey: serie.id,
        id: serie.id,
        label: serie.name,
        valueFormatter: (value: number | null) =>
          value == null || value === undefined ? "–" : value.toString(),
      })),
    ...(showMidnight
      ? [
          {
            color: "black",
            connectNulls: false,
            curve: "monotoneX" as CurveType,
            dataKey: "midnight",
            showMark: false,
            area: true,
            id: "midnight",
            disableHighlight: true,
          },
        ]
      : []),
    ...(showNight
      ? [
          {
            color: theme.palette.grey[500],
            connectNulls: false,
            curve: "monotoneX" as CurveType,
            dataKey: "night",
            showMark: false,
            area: true,
            id: "night",
            disableHighlight: true,
          },
        ]
      : []),
    ...(!legend.hidden?.includes("extras")
      ? [
          {
            color: theme.palette.grey[500],
            connectNulls: false,
            curve: "monotoneX" as CurveType,
            dataKey: "extras",
            id: "extras",
          },
        ]
      : []),
  ];

  return { chartData, chartSeries };
};
